import React, { Suspense, lazy } from 'react'
import { Switch, Redirect, AuthRoute, Route } from '@fs/zion-router'
import { NotFound, ErrorPage } from '@fs/zion-error-page'
import { NoticeLoading } from '@fs/zion-icon'
import { css } from '@emotion/core'
import { Title } from '@fs/zion-head'
import { useTranslation } from 'react-i18next'
import { boolAttr } from '@fs/zion-frontend-friends'
import { Row, useContainerWidth } from '@fs/zion-ui'
import { useFeatureFlag } from '@fs/zion-flags'
import { i18n } from '@fs/zion-locale'
import { useCanAddPersonalData } from '@fs/zion-permissions'
import { CountsProvider } from './components/useReservationCounts'
import { GroupsProvider } from './components/useGroupsState'
import { ReservationsProvider } from './components/useReservationsContext'
import {
  useIsAuthorizedToViewTemple,
  validBuildTemplePageLangForLocale,
} from './components/useIsAuthorizedToViewTemple'
import ViewWrapper from './components/ViewWrapper/ViewWrapper'
import IsNavDrawerHiddenContext from './components/IsNavDrawerHiddenContext'
import NavDrawer from './components/NavDrawer/NavDrawer'

const Completed = lazy(() => import('./pages/Completed/Completed'))
const OrdinancesReady = lazy(() => import('./pages/OrdinancesReady/OrdinancesReady'))
const ReceiveAName = lazy(() => import('./pages/ReceiveAName/ReceiveAName'))
const MyReservations = lazy(() => import('./pages/MyReservations/MyReservations'))
const SharedWithTemple = lazy(() => import('./pages/SharedWithTemple/SharedWithTemple'))
const FamilyGroups = lazy(() => import('./pages/FamilyGroups/FamilyGroups'))
const ScheduleTempleAppointment = lazy(() => import('./pages/ScheduleTempleAppointment/ScheduleTempleAppointment'))

const templePageLayoutCss = css`
  position: relative;
  min-height: 100vh;
  max-width: 100vw;
  display: grid;
  grid-template-columns: max-content 1fr;
  &[is-nav-bar-hidden] {
    display: block;
  }
  @media print {
    display: block;
  }
`

const LargeLoadingSpinner = () => (
  <Row alignX="center">
    <NoticeLoading size="lg" />
  </Row>
)

const NonReligiousDevError = () => {
  return (
    <ErrorPage
      image="unauthorized"
      heading="User is in non religious mode"
      body={
        <div>
          This only shows in dev mode. <br />
          <br />
          Usually it will redirect to the home page
        </div>
      }
    />
  )
}

const NonMemberDevError = () => {
  return (
    <ErrorPage
      image="unauthorized"
      heading="User is non-member"
      body={
        <div>
          This only shows in dev mode. <br />
          <br />
          Usually it will redirect to{' '}
          <a href="https://www.churchofjesuschrist.org/temples/why-latter-day-saints-build-temples">
            https://www.churchofjesuschrist.org/temples/why-latter-day-saints-build-temples
          </a>
        </div>
      }
    />
  )
}

function App() {
  const [t] = useTranslation()
  const { state, loading, error } = useIsAuthorizedToViewTemple() ?? {}
  const { canAccessTemple, isNonReligiousMode } = state ?? {}
  const { isOn: russianAccessInOn } = useFeatureFlag('treeWeb_header_showTempleLinksRussia')
  const [canAddPersonalData] = useCanAddPersonalData()
  const atWidth = useContainerWidth()
  const isNavDrawerHidden = atWidth({ default: true, xl: false })

  if (loading) {
    return <LargeLoadingSpinner />
  }
  if (error) {
    return <ErrorPage />
  }

  // Non-religious mode means the user is from a country where we don't want to show any religious content and the user is not a member of the church
  if (isNonReligiousMode) {
    if (process.env.NODE_ENV === 'development') {
      return <NonReligiousDevError />
    }
    window.location.replace(window.location.origin)
    return null
  }

  // This means the user is a non-member
  if (!canAccessTemple) {
    if (process.env.NODE_ENV === 'development') {
      return <NonMemberDevError />
    }
    const validLangCode = validBuildTemplePageLangForLocale[i18n.language] || 'eng'
    window.location.replace(
      `https://www.churchofjesuschrist.org/temples/why-latter-day-saints-build-temples?lang=${validLangCode}`
    )
    return null
  }
  const hideFromBlockedCountries = !canAddPersonalData && !russianAccessInOn
  return (
    <CountsProvider>
      <GroupsProvider>
        <ReservationsProvider>
          <IsNavDrawerHiddenContext.Provider value={hideFromBlockedCountries || isNavDrawerHidden}>
            <Title>{t('tw-temple.page-title')}</Title>
            <div
              css={templePageLayoutCss}
              is-nav-bar-hidden={boolAttr(hideFromBlockedCountries || isNavDrawerHidden)}
              data-testid="temple-table"
            >
              {!isNavDrawerHidden && <NavDrawer canAddPersonalData={canAddPersonalData} />}

              <ViewWrapper>
                <Suspense fallback={<LargeLoadingSpinner />}>
                  <Switch>
                    <AuthRoute path="/reservations" requirePersonalData={!russianAccessInOn}>
                      <MyReservations />
                    </AuthRoute>

                    <AuthRoute path="/shared" requirePersonalData={!russianAccessInOn}>
                      <SharedWithTemple />
                    </AuthRoute>

                    <AuthRoute exact path={['/groups/:groupId', '/groups']} requirePersonalData>
                      <FamilyGroups />
                    </AuthRoute>

                    <AuthRoute path="/completed" requirePersonalData={!russianAccessInOn}>
                      <Completed />
                    </AuthRoute>

                    <AuthRoute
                      exact
                      path={[
                        '/ordinances-ready/:ordinanceType(baptism-and-confirmation|initiatory|endowment|sealing-to-parents|sealing-to-spouse)',
                        '/ordinances-ready',
                      ]}
                    >
                      <OrdinancesReady />
                    </AuthRoute>

                    <AuthRoute path="/appointment" requirePersonalData={!russianAccessInOn}>
                      <ScheduleTempleAppointment />
                    </AuthRoute>

                    <AuthRoute path="/receive-a-name" requirePersonalData={!russianAccessInOn}>
                      <ReceiveAName />
                    </AuthRoute>

                    <Redirect path="/ordinancesready" to="/ordinances-ready" />
                    <Redirect exact path="/" to="/reservations" />
                    <Route path="*">
                      <NotFound />
                    </Route>
                  </Switch>
                </Suspense>
              </ViewWrapper>
            </div>
          </IsNavDrawerHiddenContext.Provider>
        </ReservationsProvider>
      </GroupsProvider>
    </CountsProvider>
  )
}
export default App
